import React, {useEffect, useMemo, useState} from "react";
import LaunchIcon from "@mui/icons-material/Launch";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import {useTranslation} from "react-i18next";
import CustomDialog from "../custom-dialog";
import CustomDialogTitle from "../custom-dialog-title";
import {getDatasetSize} from "../../utils/dataset";

const getUpdatedLayoutFromSelected = (layout, selected) => ({
  ...layout,
  cols: (layout?.cols || []).filter(dim => !selected.includes(dim)),
  rows: (layout?.rows || []).filter(dim => !selected.includes(dim)),
  sections: (layout?.sections || []).filter(dim => !selected.includes(dim)),
  filters: [...(layout?.filters || []), ...selected]
});

const TableLayoutLight = ({open, onClose, onSubmit, onLayoutOpen, layout, dimensionsInfo, limit}) => {
  const {t} = useTranslation();

  const [selected, setSelected] = useState(null);

  const dimensions = useMemo(
    () =>
      [...(layout?.cols || []), ...(layout?.rows || []), ...(layout?.sections || [])].sort(
        (a, b) => dimensionsInfo?.[b]?.size - dimensionsInfo?.[a]?.size
      ),
    [layout, dimensionsInfo]
  );

  useEffect(() => {
    if (open) {
      setSelected(prevVal => {
        if (prevVal !== null) {
          return prevVal;
        } else {
          const newSelected = [];
          dimensions.forEach(dim => {
            const newLayout = getUpdatedLayoutFromSelected(layout, newSelected);
            if (getDatasetSize(dimensionsInfo, newLayout) > limit) {
              newSelected.push(dim);
            }
          });
          return newSelected;
        }
      });
    }
    return () => {
      setSelected(null);
    };
  }, [open, dimensions, dimensionsInfo, layout, limit]);

  if (!layout || !selected) {
    return null;
  }

  const newLayout = getUpdatedLayoutFromSelected(layout, selected);
  const datasetSize = getDatasetSize(dimensionsInfo, newLayout);

  return (
    <CustomDialog id="table-layout-light__dialog" open={open} onClose={onClose}>
      <CustomDialogTitle onClose={onClose}>{t("components.tableLayoutLight.title")}</CustomDialogTitle>
      <DialogContent>
        <Grid container spacing={1}>
          {(dimensions || []).map((dim, idx) => (
            <Grid key={idx} size={12} container spacing={1} alignItems="center">
              <Grid>
                <Checkbox
                  id={`table-layout-light__checkbox-${dim[idx]}`}
                  style={{padding: 6}}
                  checked={selected.includes(dim)}
                  onChange={(ev, checked) =>
                    checked ? setSelected([...selected, dim]) : setSelected(selected.filter(sel => sel !== dim))
                  }
                />
              </Grid>
              <Grid>{`${dimensionsInfo?.[dim]?.label} (${dimensionsInfo?.[dim]?.size})`}</Grid>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container justifyContent="space-between">
          <Grid>
            {onLayoutOpen && (
              <Button
                onClick={() => {
                  onClose();
                  onLayoutOpen();
                }}
                endIcon={<LaunchIcon />}
              >
                {t("components.tableLayoutLight.buttons.openAdvancedMode")}
              </Button>
            )}
          </Grid>
          <Grid>
            <Button id="table-layout-light__close-button" onClick={onClose}>
              {t("commons.confirm.close")}
            </Button>
            <Button
              id="table-layout-light__confirm-button"
              onClick={() => {
                onClose();
                onSubmit(newLayout);
              }}
              disabled={limit && datasetSize > limit}
            >
              {t("commons.confirm.confirm")}
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </CustomDialog>
  );
};

export default TableLayoutLight;
